import { Component, EventEmitter, inject, Input, OnInit, Output, signal, WritableSignal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { loadScript } from '@paypal/paypal-js';
import { PAYPAL_INNATELY_CLIENT_ID, PAYPAL_INNATELY_VENDOR_CLIENT_ID } from '@equine-ui-nx/injection-tokens';
import { EntryPaymentsService } from '../../data-access/entryPayments.service';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PaymentErrorsMapper } from '../../interfaces/payment-errors-mapper';
import { MatButtonModule } from '@angular/material/button';
import { AmountPipe } from '../../pipes';


@Component({
  selector: 'equine-ui-nx-show-entry-pay-with-cc',
  standalone: true,
  imports: [CommonModule, MatButtonModule, AmountPipe],
  templateUrl: './show-entry-pay-with-cc.component.html',
  styleUrl: './show-entry-pay-with-cc.component.scss',
})
export class ShowEntryPayWithCcComponent implements OnInit {

  @Input({ required: true }) showPaymentDetailsIds!: string[];
  @Input() fromEntryFee = false;
  @Output() success = new EventEmitter<void>();
  @Output() payingStatus = new EventEmitter<boolean>();

  private entryPaymentsService = inject(EntryPaymentsService);
  private paypalInnatelyClientId = inject(PAYPAL_INNATELY_CLIENT_ID);
  private paypalInnatelyVendorClientId = inject(PAYPAL_INNATELY_VENDOR_CLIENT_ID);
  
  paying = signal(false);
  cardFields: any;
  orderId!: string;
  private router = inject(Router);
  private _snackBar = inject(MatSnackBar);
  errors: WritableSignal<{ key: string, value: string }[]> = signal<{ key: string, value: string }[]>([]);
  loaded = signal(false);

  ngOnInit(): void {
    this.loadPaypalCard();
  }

  loadPaypalCard() {
    loadScript({
      clientId: this.fromEntryFee ? this.paypalInnatelyVendorClientId : this.paypalInnatelyClientId,
      components: ['card-fields'],
      intent: 'capture'
    }).then((paypal) => {

      this.cardFields = (paypal as any).CardFields({
        createOrder: () => {
          this.paying.set(true);
          this.payingStatus.emit(this.paying());
          return new Promise((resolve, reject) => {
            if (this.fromEntryFee) {
              this.entryPaymentsService.getPaypalOrderForShowFee(this.showPaymentDetailsIds, true)
                .subscribe({
                  next: (response) => {
                    debugger
                    resolve(response.order.id);
                  },
                  error: (error) => {
                    reject(error);
                  }
                });
            } else {
              this.entryPaymentsService.getPaypalOrderId(this.showPaymentDetailsIds, true)
                .subscribe({
                  next: (order) => {
                    resolve(order.paypalOrder.id);
                  },
                  error: (error) => {
                    reject(error);
                  }
                });
            }
          });
        },
        onApprove: (orderData: any) => {
          return new Promise((resolve, reject) => {
            if (this.fromEntryFee) {
              this.entryPaymentsService.captureShowFees(orderData.orderID, this.showPaymentDetailsIds)
                .subscribe({
                  next: (success) => {
                    this.success.emit();
                    resolve(success);
                  },
                  error: (error) => {
                    reject(error);
                  }
                });
            } else {
              this.entryPaymentsService.captureEntries(orderData.orderID, this.showPaymentDetailsIds)
                .subscribe({
                  next: (success) => {
                    this.router.navigate(['payments', 'success']);
                    this.success.emit();
                    resolve(success);
                  },
                  error: (error) => {
                    reject(error);
                  }
                });
            }
          });
        },
        onError: (error: any) => {
          this.paying.set(false);
          this.payingStatus.emit(this.paying());
          this.parsePaypalErrors(error);
        },
      });

      // Render each field after checking for eligibility
      if (this.cardFields.isEligible()) {
        const nameField = this.cardFields.NameField({
          placeholder: 'Your name'
        });
        nameField.render("#card-name-field-container");

        const numberField = this.cardFields.NumberField({
          placeholder: '4111 1111 1111 1111'
        });
        numberField.render("#card-number-field-container");

        const cvvField = this.cardFields.CVVField({
          placeholder: '123'
        });
        cvvField.render("#card-cvv-field-container");

        const expiryField = this.cardFields.ExpiryField({
          placeholder: '12/34'
        });
        expiryField.render("#card-expiry-field-container");
      }

      setTimeout(() => {
        this.loaded.set(true);
      }, 2000);
    });

  }

  parsePaypalErrors(error: any) {
    console.log(error);
    try {
      if (typeof error?.error?.message === 'string') {
        this._snackBar.open(`Error: an error occured. Please check your credit card information and try again`, 'Dismiss');

        // const jsonString = error.error.message.match(/\{.*\}/gm);
        // const jsonObj = JSON.parse(jsonString);

        // if (jsonObj.details) {
        //   this._snackBar.open(`${jsonObj.details[0].issue}: ${jsonObj.details[0].description}`, 'Dismiss');
        // } else {
        //   this._snackBar.open(`Error: an error occured. Please check your credit card information and try again`, 'Dismiss');
        // }
      } else {
        this._snackBar.open(`Error: an error occured. Please try again`, 'Dismiss')
      }
    } catch (error) {
      try {
        if (`${error}`.includes('post-robot')) {
          void 0;
        } else {
          this._snackBar.open(`Error: an error occured. Please try again`, 'Dismiss')
        }
      } catch (error) {
        this._snackBar.open(`Error: an error occured. Please try again`, 'Dismiss')
      }
    }
  }

  onSubmit() {
    this.cardFields.getState()
      .then((data: any) => {
        if (!data.isFormValid) {
          this.handleErrors(data.errors);
        } else {
          this.submitForm();
        }
      })
      .catch((error: any) => {
        this.parsePaypalErrors(error);
      });
  }

  handleErrors(errors: string[]) {
    const errorMessages = errors.map((each) => {
      if (each) {
        return {
          key: each,
          value: PaymentErrorsMapper[each]
        }
      } else {
        return null;
      }
    }).filter(each => !!each);

    this.errors.set(errorMessages as { key: string, value: string }[]);
  }

  submitForm() {
    this.errors.set([]);
    // This calls createOrder method
    this.cardFields.submit()
      .then(() => {
        this._snackBar.open(`Success!`, 'Dismiss', {
          duration: 5000,
          panelClass: ['success-snackbar'],
        });
        if (!this.fromEntryFee) {
          setTimeout(() => {
            this.router.navigate(['payments', 'success'])
          }, 10);
        }
      })
      .catch((error: any) => {
        this.parsePaypalErrors(error);
      });
  }

}
